import ImageLayer from 'ol/layer/Image';
import ImageState from 'ol/ImageState';
import { containsExtent } from 'ol/extent';
import BudgetedCanvasSource, { WARP_RATIO } from './budgetedCanvasSource';
import StickyImageWMS from './stickyImageWMS';
import FlowQueue from './flowQueue';
import computeRequestShape, { getMapMaxSize } from '../wms/requestShape';
import { FRAME_COUNT } from '../constants';

const SYNC_PARAM_KEYS = ['LAYERS', 'STYLES', 'FORMAT', 'ELEVATION'];

// MeteoCore server contract: after a re-anchor, fetch the displayed
// timestep first and backfill the rest outward from it with at most this
// many GetMaps in the air at once.
const MAX_IN_FLIGHT = 4;

function pickSyncParams(params) {
  const out = {};
  for (const k of SYNC_PARAM_KEYS) {
    if (k in params) out[k] = params[k];
  }
  return out;
}

// Stable serialization of a per-slot override (see setSlotParamsProvider)
// used to detect identity changes without deep-comparing params objects.
function overrideKeyOf(o) {
  if (o.skip) return 'skip';
  if (!o.params) return '';
  return `L=${o.params.LAYERS}|R=${o.params.DIM_REFERENCE_TIME}`;
}

// Pool of N invisible ImageLayers, each with its own ImageWMS source
// pinned to a specific TIME. All 13 sources preload in parallel (the
// layers are hidden, so we drive loads manually via source.getImage +
// image.load() on window change and moveend).
//
// On each animation tick, tryShowTime(t) finds the slot whose source has
// a LOADED image covering the current view; if found, the primary layer's
// source is re-pointed at that slot's source and the primary instantly
// displays the preloaded frame with no refetch. If no slot is ready, the
// primary stays on whichever slot it last displayed — the tick produces
// no visible change rather than a blank.
//
// Window slides (IS_FOLLOWING pulling in new data) preserve 12 of 13
// slots: we match existing slot.time values to the new window and only
// refetch the one slot whose TIME left the window.
export default class FramePool {
  constructor({
    primaryLayer, map, size = FRAME_COUNT, ratio = 1.5, interpolator = null,
  }) {
    this.primary = primaryLayer;
    this.map = map;
    this.size = size;
    this.slots = [];
    this.ratio = ratio;
    // Current request shape ({ dpr, ratio } from src/wms/requestShape.js);
    // derived lazily in _getViewContext, null until the first load path runs.
    this._shape = null;
    // Current quantized request anchor (see _updateAnchor), null until the
    // first load path runs.
    this._anchor = null;
    // Optional RadarInterpolator instance. When present, showInterpolated
    // drives the interpolator's warp shader and a separate warp layer
    // displays the interpolated canvas.
    this.interpolator = interpolator;
    this._flowQueue = new FlowQueue();
    this.warpLayer = null;
    this.interpActive = false;
    // Updated each RAF so the warp source's canvasFunction can read
    // the current (A, B, t) it should render. Kept as an object
    // reference so closures in setInterpolator can read through it.
    this._warpState = { timeA: null, timeB: null, t: 0 };

    this.onLoadStateChange = null;
    // Per-index callback fired when the pair starting at that index
    // may have changed flow-ready status — both endpoints loaded
    // AND the interpolator has a flow pair for (windowTimes[i],
    // windowTimes[i+1]). Radar.js uses it to paint a "loaded but
    // no-flow" indicator on the timeline.
    this.onFlowStateChange = null;
    this.windowTimes = null;
    this.currentTime = null;
    // Optional per-slot identity provider (see setSlotParamsProvider).
    // Null = every slot mirrors the primary — the classic behavior.
    this._slotParamsProvider = null;
    // Reentrancy depth for pool-originated source writes, so the primary
    // 'change' watcher only diffs EXTERNAL param changes (updateLayer,
    // style picks) and never the pool's own per-slot writes.
    this._selfUpdate = 0;

    const baseSource = primaryLayer.getSource();
    for (let i = 0; i < size; i++) {
      const source = new StickyImageWMS({
        url: baseSource.getUrl(),
        params: { ...baseSource.getParams() },
        attributions: baseSource.getAttributions(),
        ratio,
        hidpi: false,
        serverType: 'geoserver',
      });
      const slot = {
        layer: null, source, time: null, loaded: false, skipped: false, overrideKey: '',
      };
      const layer = new ImageLayer({
        name: `${primaryLayer.get('name')}__slot${i}`,
        visible: false,
        opacity: 0,
        source,
      });
      slot.layer = layer;
      // Only act on events for the source's CURRENT image. When the
      // user pans/zooms rapidly, old requests get superseded by newer
      // ones (source.image points at the newer one); the old requests
      // keep loading in the background and eventually fire completion
      // events for the OLD extent. Acting on those would mark the slot
      // "loaded" and promote a stale image to sticky prematurely.
      source.on('imageloadstart', (event) => {
        if (event.image !== slot.source.image) return;
        slot.loaded = false;
        this._notifyLoadChange(slot);
      });
      source.on('imageloadend', (event) => {
        if (event.image !== slot.source.image) return;
        slot.loaded = true;
        // Update sticky here so slots that are never primary (OL never
        // calls getImage on invisible layers) still advance their
        // sticky when their current image finishes loading.
        slot.source.setSticky(event.image);
        this._notifyLoadChange(slot);
        // Sticky override masks OL's IDLE tracking — the renderer
        // never attached a CHANGE listener to the new image, so it
        // doesn't know the new image landed. If this slot is the one
        // primary is currently using, force the layer to re-render by
        // marking it changed; otherwise a plain map.render() suffices.
        if (this.primary.getSource() === slot.source) {
          this.primary.changed();
        }
        this.map.render();
        // Hand the fresh bitmap to the interpolator so it can upload
        // a GL texture keyed by the slot's TIME. Pass the extent the
        // image was loaded at too, so hasFlow can detect when the
        // view has since moved to a different extent (zoom/pan
        // pending refetch).
        if (this.interpolator) {
          this.interpolator.onSlotLoaded(slot, event.image.getImage(), event.image.getExtent());
          // Compute flow for pairs neighbouring this time that now
          // have both endpoints loaded. The interpolator decides
          // whether to run LK or zero-fill based on its useFlow flag.
          this._checkFlowCompute(slot.time);
        }
        // Backfill: each completion frees a slot under the in-flight
        // cap; keep pumping outward until all 13 frames are loaded.
        this._pumpLoads();
      });
      source.on('imageloaderror', (event) => {
        if (event.image !== slot.source.image) return;
        slot.loaded = false;
        this._notifyLoadChange(slot);
      });
      this.slots.push(slot);
      map.addLayer(layer);
    }

    // Debounce moveend-driven invalidate+prefetch AND coalesce across
    // a whole wheel-zoom gesture. OL fires a moveend at the end of
    // every view-animation tween, and a scroll-wheel zoom is a train
    // of such tweens. A moveend-only debounce expires in the gap
    // between tweens during a slow scroll, so each wheel tick ends
    // up firing its own prefetch burst. We also listen to raw DOM
    // `wheel` events and restart the timer on each one — the
    // gesture only "ends" after QUIET_MS of no wheel AND no moveend.
    // While the timer is pending we treat the pool as mid-gesture:
    // prefetch, frontier-advance and playback-advance are all
    // suppressed. Sticky-while-loading covers the user until the
    // flush runs.
    const QUIET_MS = 300;
    this._moveendTimer = null;
    this._gestureEndAt = 0;
    const scheduleFlush = () => {
      if (this._moveendTimer) clearTimeout(this._moveendTimer);
      this._gestureEndAt = performance.now() + QUIET_MS;
      this._moveendTimer = setTimeout(() => {
        this._moveendTimer = null;
        this._gestureEndAt = 0;
        this._handleMoveend();
      }, QUIET_MS);
    };
    map.on('moveend', scheduleFlush);
    const viewport = map.getViewport && map.getViewport();
    if (viewport) {
      viewport.addEventListener('wheel', scheduleFlush, { passive: true });
    }

    // When the layer becomes visible after being hidden, catch up on
    // any view changes that happened while hidden.
    this.primary.on('change:visible', () => {
      if (this.primary.getVisible()) this._pumpLoads();
    });

    this._primaryParamsSnap = this._snapPrimary();
    this._watchPrimarySource();
    this.primary.on('change:source', () => {
      // A showTime swap mounts a slot whose params are the pool's own
      // per-slot truth (possibly an overridden LAYERS in nowcast mode),
      // not a user product change. Re-snap before re-binding so the diff
      // baseline follows the mounted source — otherwise every swap across
      // a per-slot identity boundary would read as an external LAYERS
      // change and trigger a full resync + flow-cache drop.
      this._primaryParamsSnap = this._snapPrimary();
      this._watchPrimarySource();
    });
  }

  _snapPrimary() {
    const p = this.primary.getSource();
    const pp = p.getParams();
    return {
      url: p.getUrl(),
      layers: pp.LAYERS,
      styles: pp.STYLES,
      format: pp.FORMAT,
      elevation: pp.ELEVATION,
    };
  }

  _watchPrimarySource() {
    const p = this.primary.getSource();
    if (this._psListener) this._psListener.target.un('change', this._psListener.fn);
    const fn = () => {
      if (this._selfUpdate) return;
      const snap = this._snapPrimary();
      const prev = this._primaryParamsSnap;
      if (
        snap.url !== prev.url
        || snap.layers !== prev.layers
        || snap.styles !== prev.styles
        || snap.format !== prev.format
        || snap.elevation !== prev.elevation
      ) {
        this._primaryParamsSnap = snap;
        this._resyncAllParams(prev, snap);
      }
    };
    p.on('change', fn);
    this._psListener = { target: p, fn };
  }

  // ---- Per-slot identity overrides --------------------------------------
  //
  // A provider lets one window mix WMS identities per frame: nowcast mode
  // serves past frames from the observed composite and future frames from
  // the forecast product pinned to one model run. The provider is a pure
  // function of the frame time:
  //   provider(timeISO) -> null                    follow the primary (default)
  //                      | { skip: true }          no data at this time — never fetch
  //                      | { params: { LAYERS, DIM_REFERENCE_TIME } }
  // Only LAYERS and DIM_REFERENCE_TIME may be overridden (overrideKeyOf
  // serializes exactly those); STYLES / FORMAT / ELEVATION always sync from
  // the primary so a style pick restyles both halves of a mixed window.

  setSlotParamsProvider(fn) {
    this._slotParamsProvider = fn || null;
    this.refreshSlotOverrides();
  }

  _overrideFor(timeISO) {
    if (!this._slotParamsProvider || !timeISO) return { skip: false, params: null };
    const o = this._slotParamsProvider(timeISO);
    if (!o) return { skip: false, params: null };
    if (o.skip) return { skip: true, params: null };
    return { skip: false, params: o.params || null };
  }

  // Build a slot's full params object. The literal's key order is fixed
  // (sync keys → TIME → DIM_REFERENCE_TIME → override) so params_ first-
  // insertion order — and with it the GetMap query-string order — is
  // byte-identical for every slot in every pane; the shared blob cache and
  // the server's exact-URL cache both key on the full URL string.
  // DIM_REFERENCE_TIME: undefined keeps that key's position stable AND
  // scrubs a stale run pin when a slot flips from forecast back to
  // observed (updateParams merges; ol/uri drops undefined from the URL).
  _mergedParamsFor(slot, o) {
    return {
      ...pickSyncParams(this.primary.getSource().getParams()),
      TIME: slot.time || undefined,
      DIM_REFERENCE_TIME: undefined,
      ...(o.params || {}),
    };
  }

  // The ONE path that changes a slot's WMS identity (product / model run /
  // TIME): guarded params write + sticky invalidation + load and
  // interpolator bookkeeping.
  _applySlotIdentity(slot, o, key) {
    const p = this.primary.getSource();
    this._selfUpdate++;
    try {
      if (slot.source.getUrl() !== p.getUrl()) slot.source.setUrl(p.getUrl());
      slot.source.updateParams(this._mergedParamsFor(slot, o));
    } finally {
      this._selfUpdate--;
    }
    // A write to the mounted source must fold into the diff baseline so it
    // never surfaces as an external change.
    if (slot.source === this.primary.getSource()) {
      this._primaryParamsSnap = this._snapPrimary();
    }
    // The slot now answers for a different identity; its previous sticky
    // would render the wrong product/run until the new image lands.
    slot.source.invalidateSticky();
    slot.skipped = o.skip;
    slot.overrideKey = key;
    slot.loaded = false;
    if (this.interpolator && slot.time) this.interpolator.invalidateSlot(slot.time);
    this._notifyLoadChange(slot);
  }

  // Re-evaluate the provider for every slot and re-identify those whose
  // override changed. This is the model-run rollover path (same TIMEs, new
  // DIM_REFERENCE_TIME) and the mode enter/exit path when no window motion
  // happens to run the setWindow diff. Quiet no-op when nothing changed.
  refreshSlotOverrides() {
    let changed = false;
    for (const slot of this.slots) {
      if (!slot.time) continue; // eslint-disable-line no-continue
      const o = this._overrideFor(slot.time);
      const key = overrideKeyOf(o);
      if (key !== slot.overrideKey) {
        this._applySlotIdentity(slot, o, key);
        changed = true;
      }
    }
    if (changed) {
      this._pumpLoads();
      this._notifyAllFlowState();
    }
  }

  _resyncAllParams(prev, now) {
    this._flowQueue.clear();
    const p = this.primary.getSource();
    for (const slot of this.slots) {
      const o = this._overrideFor(slot.time);
      // Without a provider the mounted source is already up-to-date (it
      // just received the external write that triggered this resync) —
      // skip it, exactly the classic behavior. With a provider, per-slot
      // overrides may need to reshape even the mounted source's params.
      if (slot.source !== p || this._slotParamsProvider) {
        this._selfUpdate++;
        try {
          if (slot.source.getUrl() !== p.getUrl()) slot.source.setUrl(p.getUrl());
          slot.source.updateParams(this._mergedParamsFor(slot, o));
        } finally {
          this._selfUpdate--;
        }
      }
      slot.skipped = o.skip;
      slot.overrideKey = overrideKeyOf(o);
      // Product/style/URL changed — any stale sticky is now for a
      // different layer and would bleed through on the next pan/zoom.
      slot.source.invalidateSticky();
    }
    // Our own writes above may have landed on the mounted source; make the
    // diff baseline reflect the final state.
    this._primaryParamsSnap = this._snapPrimary();
    // Flow is STYLES- and FORMAT-invariant: a colormap swap doesn't
    // change the underlying motion field, so the interpolator can
    // keep its flow cache and reuse it with the re-styled bitmaps
    // once they land. URL / LAYERS / ELEVATION changes cross physical
    // fields and must drop the cache.
    if (this.interpolator && prev && now) {
      const flowInvariant = (
        prev.url === now.url
        && prev.layers === now.layers
        && prev.elevation === now.elevation
      );
      this.interpolator.onParamsChanged({ flowInvariant });
      this._notifyAllFlowState();
    }
    this._pumpLoads();
  }

  _getViewContext() {
    const view = this.map.getView();
    const size = this.map.getSize();
    // Match OL's own renderer guard (hasArea). Without the zero check
    // calculateExtent collapses to [cx, cy, cx, cy] and triggerLoad fires
    // WMS GETs with WIDTH=0/HEIGHT=0 and a point BBOX.
    if (!size || size[0] <= 0 || size[1] <= 0) return null;
    // Every load path funnels through here, so the request shape is
    // re-derived at the same debounced boundaries as the loads themselves.
    // Its inputs (pane size, and the server's per-side GetMap limit, which
    // binds only for extreme aspects) are constant during an undisturbed
    // playback loop, so the shape — and with it every frame's WIDTH/HEIGHT —
    // stays frozen while the animation runs.
    this._applyRequestShape(computeRequestShape({
      width: size[0],
      height: size[1],
    }, getMapMaxSize(this.primary.getSource().getUrl())));
    const anchor = this._updateAnchor(view, size);
    return {
      extent: anchor.extent,
      resolution: view.getResolution(),
      projection: view.getProjection(),
    };
  }

  // Compute (or keep) the quantized, world-anchored request extent.
  //
  // MeteoCore contract, "Buffer anchoring": the buffered bbox must not
  // follow the exact view — its anchor snaps to a coarse world grid with
  // steps of about half the buffer margin, so panning away and back
  // reuses URLs already in the browser/server caches instead of minting
  // a unique bbox per pan. The anchor is FROZEN while the view stays
  // inside the buffer margin (in particular for the whole of an
  // undisturbed animation loop) and is only replaced when the view edge
  // crosses the margin or the resolution/request-shape changes; every
  // caller reaches this via debounced paths.
  //
  // Replacing the anchor invalidates all 13 frames at once (their bbox
  // changed, per the contract), so old wrappers are dropped and slots
  // marked stale; stickies survive so the previous frames stay on
  // screen until the new bbox loads.
  _updateAnchor(view, size) {
    const resolution = view.getResolution();
    const center = view.getCenter();
    const shape = this._shape;
    const prev = this._anchor;
    // Everything below derives from (css size, ladder resolution, shape) —
    // NOT from view.calculateExtent(). An extent-derived width wobbles in
    // its last float bits depending on where the center sits (subtracting
    // coordinates of ~1e6 m magnitude), which would perturb the grid step
    // and break the byte-identical URLs that pan-away-and-back is meant
    // to reproduce.
    const w = size[0] * resolution;
    const h = size[1] * resolution;
    const viewExtent = [
      center[0] - w / 2, center[1] - h / 2,
      center[0] + w / 2, center[1] + h / 2,
    ];
    if (
      prev
      && prev.resolution === resolution
      && prev.dpr === shape.dpr
      && prev.ratio === shape.ratio
      && containsExtent(prev.bufferedExtent, viewExtent)
    ) {
      return prev;
    }
    // Per-side buffer margin in world units. The anchor grid step is half
    // the margin, so a snap displaces the buffer by at most a quarter
    // margin and the view is always still covered. At ratio 1.0 (pixel
    // budget exhausted, no buffer) the margin is 0 and the anchor simply
    // follows the view — there is nothing to reuse without a buffer.
    const marginX = ((shape.ratio - 1) * w) / 2;
    const marginY = ((shape.ratio - 1) * h) / 2;
    let cx = center[0];
    let cy = center[1];
    if (marginX > 0) cx = Math.round(cx / (marginX / 2)) * (marginX / 2);
    if (marginY > 0) cy = Math.round(cy / (marginY / 2)) * (marginY / 2);
    const anchor = {
      extent: [cx - w / 2, cy - h / 2, cx + w / 2, cy + h / 2],
      bufferedExtent: [
        cx - w / 2 - marginX,
        cy - h / 2 - marginY,
        cx + w / 2 + marginX,
        cy + h / 2 + marginY,
      ],
      resolution,
      dpr: shape.dpr,
      ratio: shape.ratio,
    };
    this._anchor = anchor;
    for (const slot of this.slots) {
      slot.source.setAnchorExtent(anchor.extent);
      if (prev) {
        slot.source.resetImageCache();
        if (slot.loaded) {
          slot.loaded = false;
          this._notifyLoadChange(slot);
        }
        if (this.interpolator && slot.time) {
          this.interpolator.invalidateSlot(slot.time);
        }
      }
    }
    if (prev) this._notifyAllFlowState();
    return anchor;
  }

  // Fan a newly computed shape out to every slot source and keep the
  // pool's `ratio` with it. The warp source does NOT follow: it renders
  // the view at WARP_RATIO 1 whatever the fetch buffer is (renderAt maps
  // the view onto the buffered frames), so it only needs a repaint. A
  // shape change re-keys every slot's next request (new WIDTH/HEIGHT),
  // costing one window refetch — the same price as the zoom/resize that
  // caused it.
  _applyRequestShape(shape) {
    const prev = this._shape;
    if (prev && prev.dpr === shape.dpr && prev.ratio === shape.ratio) return;
    this._shape = shape;
    this.ratio = shape.ratio;
    for (const slot of this.slots) {
      slot.source.setRequestShape(shape);
    }
    if (this.warpLayer) this.warpLayer.getSource().changed();
  }

  _slotAtIndex(idx) {
    if (!this.windowTimes) return null;
    const time = this.windowTimes[idx];
    if (!time) return null;
    return this.slots.find((s) => s.time === time) || null;
  }

  isZoomGestureActive() {
    return this._gestureEndAt > 0 && performance.now() < this._gestureEndAt;
  }

  // How many slots have a request in the air right now. Counted from the
  // wrappers' live state rather than event bookkeeping, so a superseded
  // wrapper can't leak a stale count.
  _inFlight() {
    let n = 0;
    for (const slot of this.slots) {
      const { image } = slot.source;
      if (image && image.getState() === ImageState.LOADING) n++;
    }
    return n;
  }

  // Load pump (MeteoCore contract, "Buffer anchoring"): fetch the CURRENT
  // frame first — the only one the user is waiting for — then backfill
  // the window outward from it (+1, -1, +2, -2, …), keeping at most
  // MAX_IN_FLIGHT requests in the air. Called wherever loading may
  // proceed: showTime, imageloadend (each completion frees a slot under
  // the cap), debounced moveend, visibility restore and param resync.
  // Every call shares the anchor from _getViewContext, so all 13 frames
  // of a loop carry the same bbox. No wrap at the window edges: the
  // frame "after" the last one is the first, an hour away — the least
  // useful thing to fetch next.
  _pumpLoads() {
    if (!this.primary.getVisible()) return;
    if (this.isZoomGestureActive()) return;
    if (!this.currentTime || !this.windowTimes) return;
    const curIdx = this.windowTimes.indexOf(this.currentTime);
    if (curIdx < 0) return;
    const ctx = this._getViewContext();
    if (!ctx) return;
    let inFlight = this._inFlight();
    for (let k = 0; k < this.size * 2 && inFlight < MAX_IN_FLIGHT; k++) {
      const offset = k % 2 ? (k + 1) / 2 : -(k / 2);
      const idx = curIdx + offset;
      if (idx >= 0 && idx < this.size) {
        const slot = this._slotAtIndex(idx);
        if (slot && slot.time && !slot.loaded && !slot.skipped) {
          const { image } = slot.source;
          if (!image || image.getState() !== ImageState.LOADING) {
            slot.source.triggerLoad(ctx.extent, ctx.resolution, 1, ctx.projection);
            const { image: after } = slot.source;
            if (after && after.getState() === ImageState.LOADING) inFlight++;
          }
        }
      }
    }
  }

  _notifyLoadChange(slot) {
    if (!this.onLoadStateChange || !this.windowTimes) return;
    const idx = this.windowTimes.indexOf(slot.time);
    // Skipped slots (no data exists at this time for this product — e.g.
    // future frames of an observation-only layer) report "loaded" so they
    // can never pin the shared timeline cell amber.
    if (idx >= 0) this.onLoadStateChange(idx, slot.skipped ? true : slot.loaded);
  }

  // Accept a list of `size` ISO timestamps. Preserve slots whose TIME is
  // still in the new window; reassign slots whose TIME dropped out to
  // the new TIMEs that aren't already covered. Only changed slots refetch.
  setWindow(times) {
    if (times.length !== this.size) {
      throw new Error(`FramePool.setWindow: expected ${this.size} times, got ${times.length}`);
    }
    this.windowTimes = times.slice();
    this._flowQueue.retain(new Set(times.slice(0, -1).map((time, i) => `${time}|${times[i + 1]}`)));
    const desired = new Set(times);
    const assigned = new Set();
    const needAssign = [];

    for (const slot of this.slots) {
      if (slot.time && desired.has(slot.time) && !assigned.has(slot.time)) {
        assigned.add(slot.time);
      } else {
        needAssign.push(slot);
      }
    }

    const unassigned = times.filter((t) => !assigned.has(t));
    for (let j = 0; j < needAssign.length; j++) {
      const slot = needAssign[j];
      const newTime = unassigned[j];
      if (!newTime) break;
      // Capture old time BEFORE overwriting so the interpolator can
      // drop flow pairs referencing the dropped TIME. Doing this
      // after `slot.time = newTime` would lose the identity we need.
      const oldTime = slot.time;
      slot.time = newTime;
      if (this.interpolator && oldTime) {
        this.interpolator.invalidateSlot(oldTime);
      }
      // Slot now represents a different TIME; _applySlotIdentity writes
      // the new TIME (+ any per-slot override) and drops the old TIME's
      // sticky, which would render the wrong frame until the new image
      // lands.
      const o = this._overrideFor(newTime);
      this._applySlotIdentity(slot, o, overrideKeyOf(o));
    }
    // Kept slots: re-check their override key. On a window slide the same
    // ISO time can cross the observed/forecast boundary (the preserved
    // boundary slot flips from advected forecast to the measured frame),
    // or the pinned model run may have rolled while the TIMEs stayed put.
    if (this._slotParamsProvider) {
      for (const slot of this.slots) {
        if (!slot.time) continue; // eslint-disable-line no-continue
        const o = this._overrideFor(slot.time);
        const key = overrideKeyOf(o);
        if (key !== slot.overrideKey) this._applySlotIdentity(slot, o, key);
      }
    }
    // Prefetch is driven by showTime / moveend (current ± neighbors),
    // not from setWindow. Reassigned slots outside the current ± 1
    // window will load lazily when the cursor reaches them.

    // Resync load state per position. After a window slide the
    // positions in windowTimes shift, so per-slot notifications alone
    // can leave poolLoadStates positions carrying stale values from
    // the previous window.
    if (this.onLoadStateChange) {
      for (let i = 0; i < this.size; i++) {
        this.onLoadStateChange(i, this.isPositionLoaded(i));
      }
    }
    this._notifyAllFlowState();
  }

  // Point the primary at the slot matching `time`. The swap is
  // unconditional — StickyImageWMS renders the slot's previous LOADED
  // image while a new one is in flight, so the animation keeps ticking
  // through frames during pan/zoom instead of stalling on whichever
  // slot last finished.
  showTime(time) {
    const slot = this.slots.find((s) => s.time === time);
    if (!slot) return false;
    this.currentTime = time;
    this._prioritizeFlow();
    if (this.primary.getSource() !== slot.source) {
      this.primary.setSource(slot.source);
    }
    this._pumpLoads();
    return true;
  }

  // Attach an interpolator and build the warp layer. Warp overlays
  // the primary. Visibility mirrors primary's so the user's
  // layer-toggle button keeps working against the primary. Opacity
  // is handled as a clean swap: while warp is rendering content,
  // primary's opacity drops to 0; when warp has nothing to show,
  // primary is restored to the user's chosen opacity. This avoids
  // both the button-revert bug from the earlier visibility-swap
  // attempt and the double-exposure artifact of naive overlay.
  setInterpolator(interpolator) {
    if (this.interpolator === interpolator) return;
    this._flowQueue.clear();
    this._teardownWarpLayer();
    // Release the old interpolator's GPU resources before dropping
    // the reference, otherwise textures / programs / FBOs leak.
    if (this.interpolator && this.interpolator !== interpolator) {
      this.interpolator.dispose();
    }
    this.interpolator = interpolator;
    if (!interpolator) return;

    this._userOpacity = this.primary.getOpacity();
    this._settingOpacityInternally = false;
    // Publish _userOpacity as a custom property on the primary layer
    // so the playlist UI can show the user's chosen opacity even
    // when our transparent swap has the actual layer.opacity at 0.
    // Silent set — no propertychange event fires.
    this.primary.set('_userOpacity', this._userOpacity, true);

    // 1×1 transparent canvas we return from canvasFunction when the
    // warp has nothing to show. Returning null from canvasFunction
    // causes ol/source/ImageCanvas to keep its last cached canvas
    // visible — which means old warp content stays displayed after
    // stop + step. Returning a new empty canvas forces the layer to
    // display a transparent pixel instead, so primary shows through.
    this._emptyCanvas = document.createElement('canvas');
    this._emptyCanvas.width = 1;
    this._emptyCanvas.height = 1;

    // canvasFunction is given the size to render, in canvas pixels:
    // up to 2x CSS size for radar, subject to BudgetedCanvasSource's
    // pixel budget. This avoids filtering the warped edges again when
    // enlarging to a high-DPI screen. The canvas covers the
    // requested extent at that size; the layer renderer scales it onto
    // the screen through the image pixel ratio the source reports.
    const canvasFunction = (extent, resolution, pixelRatio, size) => {
      if (!this.interpActive) return this._emptyCanvas;
      const { timeA, timeB, t } = this._warpState;
      if (!timeA || !timeB) return this._emptyCanvas;
      // hasFlow checks whether the stored frames cover the VIEW (not the
      // fetch buffer) so small pans within the buffer keep flow active.
      // The view comes from the map, not from the extent OL requests
      // here: the image-layer renderer asks for more than the view, and
      // with the old 1.5× warp ratio that padding happened to be hidden
      // inside the division back to a "view" extent — at ratio 1 it is
      // not, and a padded extent is never inside a buffer sized for the
      // view, so flow would silently never engage.
      const viewExtent = this._viewExtent();
      if (!viewExtent || !interpolator.hasFlow(timeA, timeB, viewExtent)) return this._emptyCanvas;
      // Pass the canvas extent so renderAt can compute the UV
      // transform that places content at the correct world position.
      return interpolator.renderAt(timeA, timeB, t, size[0], size[1], extent);
    };

    this.warpLayer = new ImageLayer({
      name: `${this.primary.get('name')}__warp`,
      // Hidden until interp becomes active — OL skips the layer
      // entirely (no canvasFunction calls, no compositor work) while
      // playback is paused, which lets the GPU process actually idle.
      visible: false,
      opacity: this._userOpacity,
      // The view alone, under a pixel budget — see WARP_RATIO and
      // WARP_PIXEL_BUDGET. The slot images keep their own 1.5× fetch
      // ratio (this.ratio); renderAt maps the view onto them.
      source: new BudgetedCanvasSource({
        canvasFunction,
        ratio: WARP_RATIO,
        maxPixelRatio: interpolator.motionProfile === 'radar' ? 2 : 1,
      }),
    });

    this._primaryVisListener = () => {
      const visible = this.primary.getVisible();
      this.warpLayer.setVisible(this.interpActive && visible);
      if (visible) {
        // Loaded images and completed flows survive hiding. Queue only missing
        // pairs, using the current window and displayed-time priority.
        this.refreshFlows();
      } else {
        // Hidden layers must not compete with visible panes for GPU work.
        this._flowQueue.clear();
        interpolator.prefetch([]);
      }
    };
    // Opacity listener is guarded so our own transparent/restore
    // writes don't get captured as "the user's chosen opacity".
    this._primaryOpacityListener = () => {
      if (this._settingOpacityInternally) return;
      this._userOpacity = this.primary.getOpacity();
      this.primary.set('_userOpacity', this._userOpacity, true);
      this.warpLayer.setOpacity(this._userOpacity);
    };
    this.primary.on('change:visible', this._primaryVisListener);
    this.primary.on('change:opacity', this._primaryOpacityListener);
    // Insert the warp layer right after the primary so it takes
    // over the primary's z-slot. addLayer would append to the end,
    // which puts the warp above POIs, range rings and the
    // observation layer — wrong z-order. If the primary isn't in
    // the collection for some reason, fall back to appending.
    const mapLayers = this.map.getLayers();
    const primaryIdx = mapLayers.getArray().indexOf(this.primary);
    if (primaryIdx >= 0) {
      mapLayers.insertAt(primaryIdx + 1, this.warpLayer);
    } else {
      mapLayers.push(this.warpLayer);
    }

    interpolator.onContextChange = () => {
      if (this.interpolator !== interpolator) return;
      this._flowQueue.clear();
      // Loss can happen while playback is suspended, so restore primary now,
      // without waiting for the RAF clock. Clear OL's cached warp as well.
      this._setPrimaryTransparent(false);
      this._warpState.timeA = null;
      this._warpState.timeB = null;
      this.warpLayer.getSource().changed();
      if (!interpolator.contextLost) {
        this._uploadInterpolationFrames();
        this.refreshFlows();
      } else {
        this._notifyAllFlowState();
      }
      this.map.render();
    };
    this._uploadInterpolationFrames();
  }

  // Used on initial attachment and GPU restoration. The pool remains the
  // authority for images loaded during loss, window slides and identity changes.
  // Never restore a saved GPU-era snapshot of slots or trigger new WMS requests.
  _uploadInterpolationFrames() {
    for (const slot of this.slots) {
      if (!slot.loaded || slot.skipped || !slot.time) continue; // eslint-disable-line no-continue
      const wrapper = slot.source._sticky;
      if (!wrapper) continue; // eslint-disable-line no-continue
      const img = wrapper.getImage();
      if (img && img.complete && img.naturalWidth) {
        this.interpolator.onSlotLoaded(slot, img, wrapper.getExtent());
      }
    }
  }

  _teardownWarpLayer() {
    if (!this.warpLayer) return;
    // If we'd zeroed primary's opacity for warp display, restore it
    // before dropping the warp layer so the user isn't left with an
    // invisible radar.
    if (this._userOpacity !== undefined
        && this.primary.getOpacity() === 0) {
      this._settingOpacityInternally = true;
      this.primary.setOpacity(this._userOpacity);
      this._settingOpacityInternally = false;
    }
    // Clear the custom properties so the playlist falls back to
    // layer.opacity again (which is now the user's value), and so
    // any later _interpHiding check from a stale path sees a clean
    // state.
    this.primary.set('_userOpacity', undefined, true);
    this.primary.set('_interpHiding', undefined, true);
    if (this._primaryVisListener) {
      this.primary.un('change:visible', this._primaryVisListener);
      this._primaryVisListener = null;
    }
    if (this._primaryOpacityListener) {
      this.primary.un('change:opacity', this._primaryOpacityListener);
      this._primaryOpacityListener = null;
    }
    this.map.removeLayer(this.warpLayer);
    this.warpLayer = null;
    this._emptyCanvas = null;
    this._userOpacity = undefined;
    // Interp is gone → no pair can be flow-ready. Refresh the
    // timeline so the indicator drops any pending highlights.
    this._notifyAllFlowState();
  }

  // Toggle interpolated display on/off. radar.js calls this from
  // play() / stop(). Show/hide the warp layer accordingly — while
  // hidden, OL skips it during render so the GPU process can idle.
  // On stop, also restore primary's opacity so the user sees the
  // discrete frame at their chosen opacity again.
  setInterpActive(active) {
    if (this.interpActive === active) return;
    this.interpActive = active;
    if (!active) {
      this._setPrimaryTransparent(false);
      if (this.interpolator) this.interpolator.prefetch([]);
    }
    if (this.warpLayer) {
      this.warpLayer.setVisible(active && this.primary.getVisible());
      this.warpLayer.getSource().changed();
    }
  }

  // Render the warp at fractional t between current frame (A) and
  // the next in windowTimes (B). Toggles primary's opacity: zeroed
  // while warp has real content (so the user sees only the
  // interpolated frame), restored to _userOpacity otherwise.
  //
  // Loop end: the last window frame has no meaningful next frame
  // (wrapping back to the first would be a 60-minute temporal
  // discontinuity). Instead of falling through to primary on that
  // one tick — which caused a visible brightness pop because primary
  // renders sharper than warp — hold at t=1 on the previous pair.
  // Warp at t=1 equals B = current time, so the content matches the
  // adjacent warp frames and the whole loop stays on a consistent
  // rendering path.
  showInterpolated(t) {
    if (!this.interpolator || !this.interpActive) return;
    if (!this.windowTimes || !this.currentTime) return;
    if (!this.warpLayer) return;
    const curIdx = this.windowTimes.indexOf(this.currentTime);
    if (curIdx < 0) return;

    let timeA;
    let timeB;
    let effectiveT;
    if (curIdx < this.size - 1) {
      timeA = this.currentTime;
      timeB = this.windowTimes[curIdx + 1];
      effectiveT = t;
    } else if (curIdx > 0) {
      // Last frame: reuse the previous pair held at its end.
      timeA = this.windowTimes[curIdx - 1];
      timeB = this.currentTime;
      effectiveT = 1;
    } else {
      return;
    }
    if (!timeA || !timeB) return;

    // Pass the 1× view extent — hasFlow checks whether the stored
    // frames cover the visible area, not the 1.5× fetch buffer, so
    // a small pan still within the buffer keeps flow engaged. When
    // the view moves out of the buffer, hasFlow falls back to false
    // and primary's stale-while-loading sticky covers the gap.
    const viewExtent = this._viewExtent();
    const hasFlow = this.interpolator.hasFlow(timeA, timeB, viewExtent);
    this._setPrimaryTransparent(hasFlow);

    if (!hasFlow) {
      this.interpolator.prefetch([]);
      // Clear _warpState so canvasFunction's own hasFlow check sees
      // a null pair and returns _emptyCanvas. Without this the
      // closure in canvasFunction reads the PREVIOUS pair's
      // (timeA, timeB) — and if that pair still has valid flow +
      // extent for the current view, renderAt runs and the warp
      // keeps showing the previous pair's interpolation on top of
      // the primary layer. That's the "stuck frame plus next
      // frames visible" ghost: previous pair warp over current pair
      // primary, both at user opacity.
      this._warpState.timeA = null;
      this._warpState.timeB = null;
      this.warpLayer.getSource().changed();
      return;
    }

    this._warpState.timeA = timeA;
    this._warpState.timeB = timeB;
    this._warpState.t = effectiveT;

    // Warm the two images after B before the next pair transition. Loop-end
    // prefetch prepares the first pair but never computes a last-to-first flow.
    const nextIdx = Math.min(curIdx + 1, this.size - 1) + 1;
    this.interpolator.prefetch([
      this.windowTimes[nextIdx % this.size],
      this.windowTimes[(nextIdx + 1) % this.size],
    ]);

    this.warpLayer.getSource().changed();
  }

  // Run once per debounced moveend. When an interpolator is attached,
  // drops OL's cached image wrapper on every slot source (so OL
  // re-creates a fresh wrapper at the current view). Then checks each
  // slot's loaded state against the new view, invalidates interpolator
  // frames whose stickies no longer cover, then triggers prefetch
  // around the current frame.
  _handleMoveend() {
    const ctx = this._getViewContext();
    if (ctx) {
      // First, drop each source's cached ImageWMS wrapper — but ONLY
      // when an interpolator is attached. OL's own render pipeline
      // calls getImage on the primary layer's source during a pan,
      // and the cache locks in a wrapper whose extent reflects the
      // intermediate view. Without this reset, loads completing on
      // those wrappers would store mid-pan extents that fail
      // extentApproxEqual against neighbouring slots (which didn't
      // get render-hit during the pan), and the interpolator would
      // skip computeFlow for any pair involving the current slot —
      // visible as two permanently-green cells on the timeline.
      //
      // With no interpolator (interpMode 'off', the default) that
      // failure mode cannot occur, and resetting here is pure loss:
      // it nulls OL's containsExtent image-reuse, so the very next
      // hasLoadedImageForView call below is a guaranteed cache miss
      // and the whole window refetches — even for a pan that stayed
      // well inside the 1.5x fetch buffer. Keeping the cache lets a
      // within-buffer pan reuse the already-loaded image with no
      // new GetMap request. See issue #92.
      if (this.interpolator) {
        for (const slot of this.slots) {
          slot.source.resetImageCache();
        }
      }
      for (const slot of this.slots) {
        if (!slot.time) continue; // eslint-disable-line no-continue
        const fresh = slot.source.hasLoadedImageForView(
          ctx.extent,
          ctx.resolution,
          1,
          ctx.projection,
        );
        if (slot.loaded !== fresh) {
          slot.loaded = fresh;
          this._notifyLoadChange(slot);
          // Slot's sticky no longer covers the view — any flow pair
          // built from the now-stale bitmap is suspect. Drop the
          // frame texture and pair entries; onSlotLoaded will repopulate
          // when the slot refetches.
          if (!fresh && this.interpolator && slot.time) {
            this.interpolator.invalidateSlot(slot.time);
          }
        }
      }
    }
    this._pumpLoads();
    this._notifyAllFlowState();
  }

  // Called from imageloadend after a slot's bitmap is registered with
  // the interpolator. Queues computeFlow for the (prev, this) and
  // (this, next) pairs when both endpoints are loaded. No-op if the
  // interpolator isn't attached or the time isn't in the current
  // window. Non-wrapping: the last window slot doesn't pair with
  // the first.
  _checkFlowCompute(time) {
    if (!this.interpolator || !this.windowTimes) return;
    const idx = this.windowTimes.indexOf(time);
    if (idx < 0) return;
    if (idx > 0) {
      const prevTime = this.windowTimes[idx - 1];
      if (this.isTimeLoaded(prevTime)) {
        this._queueFlowCompute(prevTime, time);
      }
    }
    if (idx < this.windowTimes.length - 1) {
      const nextTime = this.windowTimes[idx + 1];
      if (this.isTimeLoaded(nextTime)) {
        this._queueFlowCompute(time, nextTime);
      }
    }
    this._prioritizeFlow();
    this._notifyAllFlowState();
  }

  _prioritizeFlow() {
    if (!this.windowTimes) return;
    const index = this.windowTimes.indexOf(this.currentTime);
    const start = Math.min(index, this.windowTimes.length - 2);
    this._flowQueue.prioritize(start < 0 ? null
      : `${this.windowTimes[start]}|${this.windowTimes[start + 1]}`);
  }

  _queueFlowCompute(timeA, timeB) {
    const { interpolator } = this;
    if (!this.primary.getVisible()) return;
    if (!interpolator || interpolator.contextLost || interpolator.hasFlow(timeA, timeB)) return;
    const a = this.slots.find((slot) => slot.time === timeA);
    const b = this.slots.find((slot) => slot.time === timeB);
    if (!a || !b || !a.loaded || !b.loaded || a.skipped || b.skipped) return;
    const imageA = a.source._sticky;
    const imageB = b.source._sticky;
    if (!imageA || !imageB) return;
    this._flowQueue.add(`${timeA}|${timeB}`, () => {
      // Loaded wrappers are identity tokens, not copied bitmaps. A newer load
      // requeues the pair; obsolete images must never run after a view/product
      // change, even before every slot's loaded flag has caught up.
      if (this.interpolator !== interpolator || interpolator.contextLost
        || !this.primary.getVisible()) return;
      const index = this.windowTimes.indexOf(timeA);
      if (index < 0 || this.windowTimes[index + 1] !== timeB) return;
      if (a.time !== timeA || b.time !== timeB || !a.loaded || !b.loaded) return;
      if (a.source._sticky !== imageA || b.source._sticky !== imageB) return;
      try {
        interpolator.computeFlow(timeA, timeB);
      } catch (error) {
        // Deferred setup disposes a failed renderer before throwing. Restore
        // ordinary WMS and cancel this pool's remaining jobs, as with failure
        // during initial attachment. Unrelated compute errors still surface.
        if (!interpolator.disposed) throw error;
        this.setInterpolator(null);
        // eslint-disable-next-line no-console
        console.warn('Interpolation motion setup failed', error);
      }
      this._notifyAllFlowState();
      this.map.render();
    });
  }

  // Queue flow for every pair in the current window whose
  // both endpoints are loaded. Called from radar.js after a mode
  // switch (crossfade ↔ flow) — the interpolator cleared its flow
  // cache, so without this the warp layer would stay blank until
  // the next frame arrived via prefetch.
  refreshFlows() {
    if (!this.interpolator || !this.windowTimes) return;
    this._flowQueue.clear();
    for (let i = 0; i < this.windowTimes.length - 1; i++) {
      const tA = this.windowTimes[i];
      const tB = this.windowTimes[i + 1];
      if (this.isTimeLoaded(tA) && this.isTimeLoaded(tB)) {
        this._queueFlowCompute(tA, tB);
      }
    }
    this._prioritizeFlow();
    this._notifyAllFlowState();
  }

  // Current map view extent in world coords (EPSG:3857 meters).
  // Returned as the 1× viewport area, not padded by this.ratio —
  // hasFlow checks whether the stored frame's extent (which does
  // include the 1.5× fetch buffer) contains this 1× view.
  _viewExtent() {
    const size = this.map.getSize();
    if (!size || size[0] <= 0 || size[1] <= 0) return null;
    return this.map.getView().calculateExtent(size);
  }

  // Flip primary's opacity between 0 (warp is rendering content) and
  // the user's chosen opacity. Guarded on two fronts:
  //   - our change:opacity listener filters on _settingOpacityInternally
  //     so it doesn't capture our writes as a new "user opacity".
  //   - we mark the layer with `_interpHiding` before the opacity write
  //     so radar.js's layerInfoPlaylist can skip the slider-UI update —
  //     otherwise the slider would jump to 0 (and the user's drag
  //     would get overridden back to 0 on the next RAF).
  _setPrimaryTransparent(transparent) {
    if (!this.warpLayer) return;
    // Write _interpHiding BEFORE the opacity-equal early return.
    // If opacity already matches the desired value but _interpHiding
    // still holds a stale truthy value from an earlier transparent
    // swap, the playlist's change:opacity filter will keep rejecting
    // the user's slider drags.
    this.primary.set('_interpHiding', transparent, true);
    const desired = transparent ? 0 : this._userOpacity;
    if (this.primary.getOpacity() === desired) return;
    this._settingOpacityInternally = true;
    this.primary.setOpacity(desired);
    this._settingOpacityInternally = false;
  }

  // Is this time's slot's current-view image loaded?
  isTimeLoaded(time) {
    const slot = this.slots.find((s) => s.time === time);
    return !!(slot && slot.loaded);
  }

  // Does the pair starting at `index` in the current window have a
  // flow computed? Used by the timeline UI to mark cells that are
  // loaded but will still "jump" during playback because the warp
  // has no interpolation to render.
  isPairFlowReady(index) {
    if (!this.interpolator || !this.windowTimes) return false;
    if (index < 0 || index >= this.windowTimes.length - 1) return false;
    const tA = this.windowTimes[index];
    const tB = this.windowTimes[index + 1];
    if (!tA || !tB) return false;
    // A pair touching a skipped slot will never animate for this pool;
    // report it flow-ready so it can't flag the cell "flow pending".
    const sA = this.slots.find((s) => s.time === tA);
    const sB = this.slots.find((s) => s.time === tB);
    if ((sA && sA.skipped) || (sB && sB.skipped)) return true;
    return this.interpolator.hasFlow(tA, tB);
  }

  _notifyAllFlowState() {
    if (!this.onFlowStateChange || !this.windowTimes) return;
    for (let i = 0; i < this.size; i++) {
      this.onFlowStateChange(i, this.isPairFlowReady(i));
    }
  }

  // Load state at timeline position (0..size-1). Skipped slots count as
  // loaded — see _notifyLoadChange.
  isPositionLoaded(index) {
    if (!this.windowTimes) return false;
    const slot = this.slots.find((s) => s.time === this.windowTimes[index]);
    if (!slot) return false;
    return slot.skipped ? true : slot.loaded;
  }
}
